import { SharedFieldsService } from "@omnia/types";
import { colorToCss } from "./color/color";
import { CroppingStyle, CropStyle, ICrop, IVariety } from "./crops";
import { FieldViewModelImpl } from "./fields/fieldViewModel";
import {
    BiomassLevel,
    CropId,
    CropMaltingOption,
    CropProtectionStatus,
    CropPurpose,
    CropResidue,
    FieldAreaType,
    FieldDrainage,
    GrassCuts,
    GrassManagement,
    GrassSiteClass,
    GrowingSeasonLength,
    NLeafAnalysis,
    PlanType,
    SoilErosionRisk,
    WindErosionRisk,
} from "./generated/enums";
import { Geometry2 } from "./geom";
import { ISoilType, ISubsoilType } from "./soil";
import { str } from "./strings";
import { isArray, isNumber } from "./utils/utils";

export interface IField extends ISpreadableArea {
    FieldId: number;
    Name: string;
    FieldNumber: string;
    FieldGroup: string;

    HoldingId: number;
    FirstYearId: number;

    AreaType: FieldAreaType;

    WholeArea: number;
    WorkingArea: number;
    BoundaryArea: number;
    NitrogenSpreadArea: number;
    CroppingArea?: number;

    NVZ: boolean;

    SoilTypeId: number;
    SoilType: ISoilType;

    SoilErosionRisk?: SoilErosionRisk | -1;
    WindErosionRisk?: WindErosionRisk | -1;
    FieldDrainage?: FieldDrainage | -1;
    WaterCourse?: boolean;
    SensitiveHabitat?: boolean;
    AncientMonument?: boolean;

    PSorpCapacity?: number;

    SubsoilTypeId: number;
    SubsoilType: ISubsoilType;

    Cropping: ICropping[];

    Boundary: Geometry2;
    IgnoreBoundary?: boolean;

    Years?: IFieldYear[];
}

export interface IFieldYear {
    FieldYearId: number;
    FieldId: number;
    YearId: number;
    Disabled: boolean;
}

export interface ICroppingHistory {
    NextIds: number[];
    NextCroppings: ICropping[];

    PreviousIds: number[];
    PreviousCroppings: ICropping[];
}

export interface ICropping extends ICroppingHistory, ISpreadableArea {
    CroppingId: number;
    FieldId: number;
    Area: number;

    YearId: number;

    MainPreviousId?: number;

    TargetYield: number;

    Destroyed: boolean;

    Crop: ICrop;
    CropId: number;
    CropName: string;
    CropDescr: string;

    Variety: IVariety;
    VarietyId: number;
    VarietyName?: string;

    Field: IField;

    StartDate: Date;
    EndDate: Date;
    SowingDate: Date;
    HarvestDate: Date;
    HarvestDataSource?: number;
    DestructionDate: Date;

    Residue: CropResidue;

    GrassManagement: GrassManagement;
    GrassCuts: GrassCuts;
    GrassSiteClass: GrassSiteClass;

    BiomassLevel: BiomassLevel;
    StrawMulch: number;
    GrowingSeasonLength: GrowingSeasonLength;
    ProtectionStatus: CropProtectionStatus;
    NLeafAnalysis: NLeafAnalysis;

    Undersown: boolean;

    MaltingOption: CropMaltingOption;
    HighNDistilling: boolean;

    NCropGroup?: number;

    Comment: number;

    Boundary: Geometry2;
    BoundaryArea: number;

    ActualYield?: number;
    ActualYieldSource?: number;
    ActualResidue?: CropResidue;

    Composition: ICroppingComposition[];

    InSeedsPlan?: boolean;
    InNutritionPlan?: boolean;
    InSprayPlan?: boolean;

    TargetpH?: number;

    HasNutritionRequirements?: boolean;

    Plans?: IPlanInfo[];

    Market?: CropPurpose;

    Style?: CroppingStyle;

    Management?: {
        Options: CroppingManagementOption[];
    };

    /**
     * User-specified "Plot ID"
     */
    PlotId: string;

    /**
     * User-specified "Batch ID"
     */
    BatchId: string;

    /**
     * User-specified "Grower"
     */
    Grower: string;

    /**
     * User-specified "Planted Volume"
     */
    PlantedVolume: string;

    /**
     * User-specified "Description"
     */
    Description: string;
}

export type CroppingUpdateModel = ICropping & {
    SessionHoldingId: number;
    SessionYearId: number;
    YearId: number;
    Crop: ICrop | null;
    CroppingIds: number[];
    EditOptions?: CroppingEditOptions;
    MultipleTargetUpdateOptions?: CroppingMultipleTargetUpdateOptions;
};

export type CroppingEditOptions = {
    UpdateCroppingBoundaries?: boolean;

    CreateOnMultipleYears?: boolean;

    /**
     * If set, the cropping area will be initialized from the boundary area,
     * only if the cropping has a boundary, and its field is using the Boundary area type
     */
    SetAreaFromBoundary?: boolean;
    /**
     * If the start/end dates are wrong, they will be automatically reset to default values
     */
    IgnoreDateErrors?: boolean;
};

export type CroppingMultipleTargetUpdateOptions = {
    /**
     * If true, copy all the properties from the "Crop" tab (except "Crop Description") to the target croppings.
     */
    CropTab?: boolean;

    /**
     * If true, copy the `Cropping.CropDescr` to the target croppings.
     */
    CropDescription?: boolean;

    /**
     * If true, copy all the properties from the "Info" tab to the target croppings.
     */
    InfoTab?: boolean;

    /**
     * If true, copy all the management options to the target croppings.
     */
    ManagementOptionsTab?: boolean;
};

export interface CroppingManagementOption {
    /** Management option id */
    Id: number;

    /** Code (not used, for traceability) */
    Code: string;

    /** Area (ha) */
    Area: number;
}

export interface IPlanInfo {
    PlanId: number;
    Type: PlanType;
    Year: number;
    Name: string;
}

export interface ISpreadableArea {
    ManureSpreadArea?: number;
    ManureSpreadAreaCalc?: number;
    ManureSpreadAreaUser?: number;
    ManureSpreadAreaOvr?: boolean;
}

export interface ICroppingComposition {
    CropId: number;
    Crop?: ICrop;

    Percentage: number;
}

export interface FieldViewModel {
    readonly yearId: number;
    readonly currentYear: boolean;
    readonly field: IField;
    readonly fieldId: number;
    readonly fieldName: string;
    readonly fieldGroup: string;
    isField(): boolean;

    readonly cropping: ICropping;
    readonly croppingId: number;
    isCropping(): boolean;
    readonly fieldVm: FieldViewModel;
    readonly authorized: boolean;

    getId(): string;
    is(cropping: boolean, id: number): boolean;

    readonly description: string;
    getDescription(short?: boolean): string;
    getCroppingDescription(
        includeFieldName?: boolean,
        includeVariety?: boolean,
        includeCrop?: boolean,
        includePlotId?: boolean
    ): string;

    visible: boolean;
    selected: boolean;

    readonly uncropped: boolean;
    readonly cropId: number;
    readonly crop: ICrop;
    getCropName(): string;
    readonly cropDescription: string;

    readonly style: CroppingStyle;
    readonly cropStyle: CropStyle;
    readonly varietyStyle: CropStyle;
    readonly color: number[];
    readonly cssColor: string;

    readonly varietyId: number;
    readonly variety: IVariety;
    getVarietyName(): string;
    readonly marketId: number;

    readonly startDate: Date;
    readonly endDate: Date;

    readonly area: number;
    readonly areaType: FieldAreaType;

    readonly croppings: FieldViewModel[];

    hasBoundary(): boolean;
    getBoundary(): Geometry2;
    readonly ignoreBoundary: boolean;

    readonly disabled: boolean;
    isDisabledOnYear(year: number): boolean;

    hasCrops(): boolean;

    getSameYearCrops(): FieldViewModel[];

    hasSiblings(): boolean;
    getSiblings(): ICropping[];
    checkDateIntersection(startDate: Date, endDate: Date): boolean;

    readonly perennial: boolean;
    getPerennialSequence(): ICropping[];

    isUsedInPlans(): boolean;
}

export function isCropping(target: IField | ICropping): target is ICropping {
    return !!target["CroppingId"];
}

export function getFieldCroppingArea(field: IField): number {
    let area: number;

    if (field) {
        switch (field.AreaType) {
            case FieldAreaType.BoundaryArea:
                area = field.BoundaryArea;
                break;

            case FieldAreaType.WorkingArea:
                area = field.WorkingArea;
                break;
        }

        if (!isNumber(area)) {
            area = field.WholeArea;
        }
    }

    return area ?? 0;
}

export function isFieldViewModel(obj: any): obj is FieldViewModel {
    return obj instanceof FieldViewModelImpl;
}

export function getCropColor(crop: ICrop): number[] {
    if (crop && crop.Style) {
        return crop.Style.Colour;
    }

    return [0xdd, 0xdd, 0xdd];
}

export function getCropCssColor(crop: ICrop) {
    var color = getCropColor(crop);
    return colorToCss(color, false);
}

export function getCropName(cropping: ICropping): string {
    if (cropping) {
        if (cropping.Crop) {
            if (cropping.CropId == CropId.Other && cropping.CropName) {
                return cropping.CropName;
            }

            if (cropping.Crop.Name) {
                return cropping.Crop.Name;
            }
        }

        if (cropping.CropName) cropping.CropName;

        return str.Uncropped;
    }
}


/**
 * Returns a function which can be used to filter authorized croppings
 */
export function getAuthorizedCroppingFilter(fieldSvc: SharedFieldsService) {

    const authorized = fieldSvc.getAuthorizedCropping()

    if (!isArray(authorized)) {
        return (croppingId: number) => true
    }

    const set = new Set(authorized)

    return (croppingId: number) => set.has(croppingId)

}